import {
  Box,
  HStack,
  Icon,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ChevronRight } from "lucide-react";

import { Chip } from "components/ds";

import {
  ELIGIBLE_ENTITLEMENT_TOOLTIP,
  hopLabel,
  isEligibleEntitlement,
  provenanceStamp,
  type MFAccessPath,
  type MFAccessPathHop,
} from "state/accessPaths";

const HopEntitlements = ({ hop }: { hop: MFAccessPathHop }) => {
  const entitlements = hop.entitlements ?? [];
  if (entitlements.length === 0) return null;

  return (
    <Wrap spacing="2xs">
      {entitlements.map((entitlement) =>
        isEligibleEntitlement(entitlement) ? (
          <WrapItem key={entitlement.id}>
            <Tooltip
              label={ELIGIBLE_ENTITLEMENT_TOOLTIP}
              placement="top"
              hasArrow
              openDelay={300}
            >
              <Box>
                <Chip tone="warning">{entitlement.name}</Chip>
              </Box>
            </Tooltip>
          </WrapItem>
        ) : (
          <WrapItem key={entitlement.id}>
            <Chip tone="default">{entitlement.name}</Chip>
          </WrapItem>
        ),
      )}
    </Wrap>
  );
};

const Hop = ({ hop }: { hop: MFAccessPathHop }) => {
  const stamp = provenanceStamp(hop.provenance);

  return (
    <VStack align="flex-start" spacing="2xs" minW={0}>
      <Text fontSize="smd" fontWeight={600} color="neutral.950" noOfLines={1}>
        {hopLabel(hop)}
      </Text>
      <HopEntitlements hop={hop} />
      {stamp && (
        <Text fontSize="xs" color="neutral.500" noOfLines={1}>
          {stamp}
        </Text>
      )}
    </VStack>
  );
};

export type AccessPathBreadcrumbsProps = { path: MFAccessPath };

const AccessPathBreadcrumbs = ({ path }: AccessPathBreadcrumbsProps) => {
  if (path.hops.length === 0) {
    return (
      <Text fontSize="xs" color="neutral.500">
        No route recorded
      </Text>
    );
  }

  return (
    <HStack align="flex-start" spacing="sm" flexWrap="wrap">
      {path.hops.map((hop, index) => (
        <HStack
          key={`${hop.accessId}-${index}`}
          align="flex-start"
          spacing="sm"
        >
          <Hop hop={hop} />
          {index < path.hops.length - 1 && (
            <Icon
              as={ChevronRight}
              boxSize={3.5}
              color="neutral.400"
              flexShrink={0}
              mt="2px"
            />
          )}
        </HStack>
      ))}
    </HStack>
  );
};

export default AccessPathBreadcrumbs;
